import dayjs, { Dayjs } from 'dayjs';

/**
 * Application-wide date format.
 *
 * ⚠ T206 (a) — THIS IS NO LONGER A PER-DEVICE PREFERENCE, AND THAT IS HIS RULING:
 * *"it should be ONE company-wide format, only admin should be able to change it."*
 *
 * **The server is the source of truth.** It lives in the `system` settings bag and
 * reaches every generated PDF through `getCompanySettings()`. `localStorage` is kept
 * ONLY as a first-paint CACHE, so a reload does not flash the wrong layout before the
 * settings request returns — it is written from the server value, never authored here.
 *
 * That distinction is the whole bug: the old text below said "Saved on this device",
 * and it was true, which is exactly why nothing he changed in Settings ever reached a
 * PDF. The value was never sent to the server, and nine backend generators each
 * hardcoded their own layout. A doc line that outlives its feature is mistake shape 9;
 * this comment and the on-screen note were changed in the SAME commit as the code.
 *
 * Long formats (with a month name) are derived from this preference too, but
 * month-name formats are unambiguous either way — which is why the backend
 * deliberately leaves `DD-MMM-YYYY` documents alone.
 */

export type DateFormatPref = 'MM/DD/YYYY' | 'DD/MM/YYYY';

export const DATE_FORMAT_OPTIONS: { value: DateFormatPref; label: string; example: string }[] = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)', example: '06/24/2026' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (International)', example: '24/06/2026' },
];

/** First-paint CACHE of the server value. NOT the source of truth (T206 a). */
const STORAGE_KEY = 'app-date-format';
const CHANGE_EVENT = 'app-date-format-change';
const DEFAULT_FORMAT: DateFormatPref = 'DD/MM/YYYY';

const isBrowser = typeof window !== 'undefined';

const isValidPref = (v: unknown): v is DateFormatPref =>
  v === 'MM/DD/YYYY' || v === 'DD/MM/YYYY';

export const getDateFormat = (): DateFormatPref => {
  if (!isBrowser) return DEFAULT_FORMAT;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isValidPref(stored) ? stored : DEFAULT_FORMAT;
  } catch {
    return DEFAULT_FORMAT;
  }
};

/**
 * Write the CACHE and tell every listener. **This does not persist anything.**
 *
 * It is called in two places and both are reads-from-authority, not authorings:
 * when the settings response arrives, and after an admin's save has been ACCEPTED by
 * the server. Nothing else may call it — a screen that writes the cache without the
 * server would show a format that no document uses, which is the original defect
 * wearing the fix's clothes. `DateFormatContext` owns the server write.
 */
export const applyDateFormat = (pref: DateFormatPref): void => {
  if (!isBrowser) return;
  if (!isValidPref(pref)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, pref);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: pref }));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
};

export const subscribeDateFormat = (listener: (pref: DateFormatPref) => void): (() => void) => {
  if (!isBrowser) return () => {};
  const handleCustom = (e: Event) => {
    const detail = (e as CustomEvent<DateFormatPref>).detail;
    if (isValidPref(detail)) listener(detail);
  };
  const handleStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && isValidPref(e.newValue)) listener(e.newValue);
  };
  window.addEventListener(CHANGE_EVENT, handleCustom);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handleCustom);
    window.removeEventListener('storage', handleStorage);
  };
};

type DateInput = string | number | Date | Dayjs | null | undefined;

const toDayjs = (input: DateInput): Dayjs | null => {
  if (input === null || input === undefined || input === '') return null;
  const d = dayjs(input);
  return d.isValid() ? d : null;
};

/** Short numeric date — respects the user's MM/DD vs DD/MM preference. */
export const formatDate = (input: DateInput, fallback = '—'): string => {
  const d = toDayjs(input);
  return d ? d.format(getDateFormat()) : fallback;
};

/**
 * ⚠ T211 — THIS NO LONGER PRINTS A MONTH NAME, AND THAT IS HIS RULING.
 * It used to render `Aug 18, 2026` while the field beside it rendered `08/18/2026`,
 * so one screen showed two layouts for the same kind of value. He asked twice for
 * every date to follow the one setting. The name is kept because ~30 call sites use
 * it and renaming them would be churn with no behaviour in it — but there is now no
 * difference between long and short, which is the point.
 */
export const formatDateLong = (input: DateInput, fallback = '—'): string =>
  formatDate(input, fallback);

/** Date + time. The DATE half follows the setting; the clock is not a setting. */
export const formatDateTime = (input: DateInput, fallback = '—'): string => {
  const d = toDayjs(input);
  if (!d) return fallback;
  return `${d.format(getDateFormat())} ${d.format('h:mm A')}`;
};
